<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Academic Years Management
                </h1>
                <button
                    @click="openModal"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Academic Year
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by name..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchAcademicYears"
                    />

                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="academicYearStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="academicYearStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="academicYearStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ academicYearStore.error }}
                        </p>
                        <button
                            @click="academicYearStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!academicYearStore.loading && !academicYearStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Start Date
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    End Date
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="academicYear in academicYearStore.academicYears"
                                :key="academicYear.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ academicYear.name }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDate(academicYear.start_date) }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ formatDate(academicYear.end_date) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="academicYear.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editAcademicYear(academicYear)"
                                        title="Edit Academic Year"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        v-if="!academicYear.is_active"
                                        @click="setActive(academicYear)"
                                        title="Set Active"
                                        class="inline-flex items-center justify-center p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors mr-2"
                                    >
                                        <CheckCircle class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(academicYear)"
                                        title="Delete Academic Year"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="academicYearStore.academicYears.length === 0"
                    class="text-center py-12"
                >
                    <Calendar class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No academic years found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new academic year.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Academic Year
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (academicYearStore.meta?.total || 0) >
                    (academicYearStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="academicYearStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            academicYearStore.meta?.current_page ===
                            academicYearStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                academicYearStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                academicYearStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                academicYearStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    academicYearStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page ===
                                    academicYearStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    academicYearStore.meta?.current_page ===
                                    academicYearStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Academic Year Modal -->
            <AcademicYearModal
                v-if="showModal"
                :academic-year="selectedAcademicYear"
                :is-editing="isEditing"
                @close="closeModal"
                @saved="handleSave"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                :show="showDeleteModal"
                title="Delete Academic Year"
                :message="
                    academicYearToDelete
                        ? `Are you sure you want to delete ${academicYearToDelete.name}? This action cannot be undone.`
                        : ''
                "
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useAcademicYearStore } from "@/stores/academicYear";
import AcademicYearModal from "@/components/AcademicYearModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import type { AcademicYear } from "@/api/academicYear";
import {
    Plus,
    RotateCcw,
    Pencil,
    CheckCircle,
    Trash2,
    Calendar,
} from "@lucide/vue";

const academicYearStore = useAcademicYearStore();
// State
const searchQuery = ref("");
const filters = ref({
    is_active: undefined as boolean | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedAcademicYear = ref<AcademicYear | null>(null);
const isEditing = ref(false);
const academicYearToDelete = ref<AcademicYear | null>(null);

// Computed

const visiblePages = computed(() => {
    const current = academicYearStore.meta?.current_page || 1;
    const last = academicYearStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.is_active !== undefined ||
        academicYearStore.page !== 1 ||
        academicYearStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    academicYearStore.perPage = value;
    academicYearStore.page = 1;
    fetchAcademicYears();
};

const resetFilters = () => {
    // Reset local component state
    searchQuery.value = "";
    filters.value.is_active = undefined;

    // Reset store state
    academicYearStore.resetFilters();
};

const fetchAcademicYears = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
        is_active: filters.value.is_active, // Explicitly pass the is_active filter
    };

    await academicYearStore.fetchAcademicYears(searchFilters);
};

const debouncedSearch = useDebounce(fetchAcademicYears, 300);

const openModal = () => {
    selectedAcademicYear.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    academicYearStore.clearError();
};

const editAcademicYear = (academicYear: AcademicYear) => {
    selectedAcademicYear.value = academicYear;
    isEditing.value = true;
    showModal.value = true;
    // Clear any previous errors when opening modal
    academicYearStore.clearError();
};

const closeModal = () => {
    showModal.value = false;
    selectedAcademicYear.value = null;
    isEditing.value = false;
};

const confirmDelete = (academicYear: AcademicYear) => {
    academicYearToDelete.value = academicYear;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    academicYearToDelete.value = null;
};

const handleSave = () => {
    closeModal();
    showNotification(
        isEditing.value
            ? "Academic year updated successfully"
            : "Academic year created successfully",
        "success",
    );
    academicYearStore.clearError();
};

const handleDelete = async () => {
    try {
        if (academicYearToDelete.value) {
            await academicYearStore.deleteAcademicYear(
                academicYearToDelete.value.id,
            );
            showNotification("Academic year deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete academic year", "error");
    }
};

const setActive = async (academicYear: AcademicYear) => {
    try {
        await academicYearStore.setActiveAcademicYear(academicYear.id);
        showNotification("Academic year set as active successfully", "success");
    } catch (error) {
        showNotification("Failed to set academic year as active", "error");
    }
};

const previousPage = () => {
    if ((academicYearStore.meta?.current_page || 1) > 1) {
        academicYearStore.setPage(
            (academicYearStore.meta?.current_page || 1) - 1,
        );
    }
};

const nextPage = () => {
    if (
        (academicYearStore.meta?.current_page || 1) <
        (academicYearStore.meta?.last_page || 1)
    ) {
        academicYearStore.setPage(
            (academicYearStore.meta?.current_page || 1) + 1,
        );
    }
};

const goToPage = (page: number) => {
    academicYearStore.setPage(page);
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchAcademicYears();
});
</script>
